import React, { useState } from 'react';
import { Clock, CheckCircle, XCircle, Eye, User, DollarSign, AlertCircle } from 'lucide-react';
import { useApprovals } from '../hooks';
import { PhotoVerificationModal, LoanApprovalModal } from '../components/modals';
import { EmployeeApproval, LoanApproval } from '../types';

const Approvals: React.FC = () => {
  const {
    employeeApprovals,
    loanApprovals,
    loading,
    error,
    approveEmployeePhoto,
    rejectEmployeePhoto,
    approveLoan,
    rejectLoan,
    refreshApprovals,
  } = useApprovals();

  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeApproval | null>(null);
  const [selectedLoan, setSelectedLoan] = useState<LoanApproval | null>(null);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showLoanModal, setShowLoanModal] = useState(false);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const [selectedLoanIds, setSelectedLoanIds] = useState<string[]>([]);

  const handleViewEmployeePhoto = (employee: EmployeeApproval) => {
    setSelectedEmployee(employee);
    setShowPhotoModal(true);
  };

  const handleViewLoan = (loan: LoanApproval) => {
    setSelectedLoan(loan);
    setShowLoanModal(true);
  };

  const handleQuickApproveEmployee = async (employee: EmployeeApproval) => {
    if (window.confirm(`Are you sure you want to approve ${employee.user.firstName} ${employee.user.lastName}'s photo?`)) {
      const success = await approveEmployeePhoto(employee.id);
      if (success) {
        alert('Employee photo approved successfully!');
      }
    }
  };

  const handleQuickRejectEmployee = async (employee: EmployeeApproval) => {
    const reason = window.prompt('Please provide a reason for rejection:');
    if (reason && reason.trim()) {
      const success = await rejectEmployeePhoto(employee.id, reason);
      if (success) {
        alert('Employee photo rejected');
      }
    }
  };

  const handleBulkApproveEmployees = async () => {
    if (selectedEmployeeIds.length === 0) return;
    
    if (window.confirm(`Are you sure you want to approve ${selectedEmployeeIds.length} employee photo(s)?`)) {
      let successCount = 0;
      for (const id of selectedEmployeeIds) {
        const success = await approveEmployeePhoto(id);
        if (success) successCount++;
      }
      setSelectedEmployeeIds([]);
      alert(`${successCount} employee photo(s) approved successfully!`);
    }
  };

  const toggleEmployeeSelection = (id: string) => {
    setSelectedEmployeeIds(prev =>
      prev.includes(id) ? prev.filter(empId => empId !== id) : [...prev, id]
    );
  };

  const toggleAllEmployees = () => {
    if (selectedEmployeeIds.length === employeeApprovals.length) {
      setSelectedEmployeeIds([]);
    } else {
      setSelectedEmployeeIds(employeeApprovals.map(emp => emp.id));
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
    }).format(amount);
  };

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Approvals</h1>
          <p className="text-gray-600">Review and manage pending approvals</p>
        </div>
        <div className="card">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="text-red-600 mb-4">
                <XCircle className="mx-auto h-12 w-12" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Approvals</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <button onClick={refreshApprovals} className="btn-primary">
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Approvals</h1>
        <p className="text-gray-600">Review and manage pending employee and loan approvals</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <User className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Photo Verifications</p>
              <p className="text-2xl font-bold text-gray-900">{employeeApprovals.length}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Loan Applications</p>
              <p className="text-2xl font-bold text-gray-900">{loanApprovals.length}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Clock className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Pending</p>
              <p className="text-2xl font-bold text-gray-900">
                {employeeApprovals.length + loanApprovals.length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Employee Photo Verification Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <h3 className="text-lg font-semibold text-gray-900">Photo Verifications</h3>
            {employeeApprovals.length > 0 && (
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedEmployeeIds.length === employeeApprovals.length}
                  onChange={toggleAllEmployees}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">
                  {selectedEmployeeIds.length === 0
                    ? 'Select All'
                    : `${selectedEmployeeIds.length} selected`}
                </span>
              </label>
            )}
          </div>
          {selectedEmployeeIds.length > 0 && (
            <button onClick={handleBulkApproveEmployees} className="btn-success">
              <CheckCircle className="h-4 w-4 mr-1" />
              Approve Selected ({selectedEmployeeIds.length})
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : employeeApprovals.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <p className="text-gray-500">No pending photo verifications</p>
          </div>
        ) : (
          <div className="space-y-4">
            {employeeApprovals.map((employee) => (
              <div key={employee.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <input
                      type="checkbox"
                      checked={selectedEmployeeIds.includes(employee.id)}
                      onChange={() => toggleEmployeeSelection(employee.id)}
                      className="h-4 w-4 mt-1 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <div className="flex-shrink-0">
                      {employee.user.profileImage ? (
                        <img
                          src={employee.user.profileImage}
                          alt={`${employee.user.firstName} ${employee.user.lastName}`}
                          className="w-16 h-16 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-gray-200 flex items-center justify-center">
                          <User className="h-8 w-8 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="text-sm font-medium text-gray-900">
                          {employee.user.firstName} {employee.user.lastName}
                        </h4>
                        <span className="text-sm text-gray-500">({employee.user.email})</span>
                      </div>
                      {employee.user.position && (
                        <p className="text-sm text-gray-600">{employee.user.position}</p>
                      )}
                      <div className="flex items-center space-x-4 mt-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          employee.kycStatus === 'VERIFIED' ? 'bg-green-100 text-green-800' :
                          employee.kycStatus === 'REJECTED' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          KYC: {employee.kycStatus}
                        </span>
                        {employee.kycData?.dojahVerificationComplete && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Dojah Verified
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Submitted: {formatDate(employee.createdAt)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleViewEmployeePhoto(employee)}
                      className="btn-secondary"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View Details
                    </button>
                    <button
                      onClick={() => handleQuickApproveEmployee(employee)}
                      className="btn-success"
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Approve
                    </button>
                    <button
                      onClick={() => handleQuickRejectEmployee(employee)}
                      className="btn-danger"
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Reject
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Loan Applications Section */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Loan Applications</h3>
        <p className="text-sm text-gray-500 mb-4">Loan approvals are handled by VPD Money admins. Employers can view the loan timeline only.</p>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : loanApprovals.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <p className="text-gray-500">No pending loan applications</p>
          </div>
        ) : (
          <div className="space-y-4">
            {loanApprovals.map((loan) => (
              <div key={loan.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div
                      className={`p-2 rounded-lg ${
                        loan.status === 'APPROVED'
                          ? 'bg-green-100'
                          : loan.status === 'UNDER_REVIEW'
                          ? 'bg-blue-100'
                          : 'bg-yellow-100'
                      }`}
                    >
                      <DollarSign
                        className={`h-6 w-6 ${
                          loan.status === 'APPROVED'
                            ? 'text-green-600'
                            : loan.status === 'UNDER_REVIEW'
                            ? 'text-blue-600'
                            : 'text-yellow-600'
                        }`}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="text-sm font-medium text-gray-900">
                          {loan.employeeName || 'Employee'}
                        </h4>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            loan.status === 'APPROVED'
                              ? 'bg-green-100 text-green-800'
                              : loan.status === 'UNDER_REVIEW'
                              ? 'bg-blue-100 text-blue-800'
                              : loan.status === 'REJECTED'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {loan.status}
                        </span>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Amount:</span> {formatCurrency(loan.requestedAmount)}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Purpose:</span> {loan.purpose}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Term:</span> {loan.term} months
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Type:</span> {loan.loanType}
                        </p>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Submitted: {formatDate(loan.createdAt)}
                      </p>
                    </div>
                  </div>
                    <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleViewLoan(loan)}
                      className="btn-primary"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View Timeline
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <PhotoVerificationModal
        isOpen={showPhotoModal}
        onClose={() => {
          setShowPhotoModal(false);
          setSelectedEmployee(null);
        }}
        employee={selectedEmployee}
        onApprove={approveEmployeePhoto}
        onReject={rejectEmployeePhoto}
      />

      <LoanApprovalModal
        isOpen={showLoanModal}
        onClose={() => {
          setShowLoanModal(false);
          setSelectedLoan(null);
        }}
        loanApplication={selectedLoan}
        onApprove={approveLoan}
        onReject={rejectLoan}
        readOnly={true}
      />
    </div>
  );
};

export default Approvals;

